import { useState } from 'react';
import { ROLES_BY_TIER, type Role, type PricingTierId } from '@seedgovernance/shared';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvited: () => void;
  tier?: PricingTierId;
}

export function InviteModal({ isOpen, onClose, onInvited, tier = 'pro' }: InviteModalProps) {
  const { currentWorkspace } = useWorkspace();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableRoles = ROLES_BY_TIER[tier] || [];
  const roleOptions = availableRoles
    .filter((r) => r !== 'owner') // Don't allow inviting as owner
    .map((r) => ({
      value: r,
      label: r.charAt(0).toUpperCase() + r.slice(1),
    }));

  function handleClose() {
    setEmail('');
    setName('');
    setRole('');
    setError(null);
    onClose();
  }

  async function handleInvite() {
    if (!currentWorkspace) return;
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!role) {
      setError('Please select a role');
      return;
    }

    setLoading(true);
    setError(null);

    const { error: apiError } = await apiClient.post(
      `/api/tenants/${currentWorkspace.tenantId}/users`,
      { email: email.trim(), name: name.trim(), role }
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      handleClose();
      onInvited();
    }
    setLoading(false);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite Team Member"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button loading={loading} onClick={handleInvite}>
            Send Invite
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        <Input
          label="Email"
          type="email"
          placeholder="colleague@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Input
          label="Name"
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <Select
          label="Role"
          placeholder="Select a role"
          options={roleOptions}
          value={role}
          onChange={(e) => setRole(e.target.value)}
        />

        {roleOptions.length === 0 && (
          <p className="text-xs text-slate-500">
            Your current plan does not support team invitations. Upgrade to add team members.
          </p>
        )}
      </div>
    </Modal>
  );
}
